import React, { useState } from "react";
import { Paper, Typography, Box, Tooltip } from "@mui/material";
import { PieChart } from "@mui/x-charts/PieChart";

const CustomPieChart = ({ data, title, onArticleClick }) => {
  const pieColors = ["#10B981", "#4B9EFF", "#8B5CF6"];
  const [selectedSliceIndex, setSelectedSliceIndex] = useState(null);
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const processedData = React.useMemo(() => {
    if (!data) return [];
    const deduplicated = data.reduce((acc, article) => {
      const existingIndex = acc.findIndex((item) => item.name === article.name);
      if (existingIndex >= 0) {
        acc[existingIndex].count += article.count;
        if (!acc[existingIndex].originalIds) {
          acc[existingIndex].originalIds = [acc[existingIndex].id, article.id];
        } else {
          acc[existingIndex].originalIds.push(article.id);
        }
      } else {
        acc.push({ ...article });
      }
      return acc;
    }, []);

    return deduplicated
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map((article) => ({
        id: article.id,
        value: article.count,
        label: article.name,
        originalData: article,
      }));
  }, [data]);

  const total = processedData.reduce((sum, entry) => sum + entry.value, 0);

  const handleClick = (item, index) => {
    setSelectedSliceIndex(index);

    if (onArticleClick && item?.originalData) {
      const formattedArticle = {
        id: item.originalData.id,
        name: item.originalData.name,
        contentId: item.originalData.contentId || item.originalData.id,
      };
      onArticleClick(formattedArticle);
    }
  };

  const handleLegendHover = (index) => {
    setHoveredIndex(index);
  };

  const handleLegendLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 2,
        height: "100%",
        minHeight: "300px",
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontSize: "0.875rem",
          fontWeight: 500,
          color: "#374151",
          marginBottom: 2,
        }}
      >
        {title}
      </Typography>
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <PieChart
          series={[
            {
              data: processedData,
              innerRadius: 60,
              outerRadius: 100,
              paddingAngle: 2,
              cornerRadius: 4,
              startAngle: -180,
              endAngle: 180,
              colors: pieColors,
              tooltip: {
                trigger: "item",
                valueFormatter: (value) => {
                  const percentage = ((value / total) * 100).toFixed(1);
                  return `${value} views (${percentage}%)`;
                },
              },
              arcLabelMinAngle: 45,
              highlightScope: { faded: "global", highlighted: "item" },
              faded: { innerRadius: 58, additionalRadius: -30, color: "gray" },
              highlighted: {
                additionalRadius: 5,
                strokeWidth: 2,
                stroke: "#000",
              },
              cx: 130, // Center the chart horizontally
            },
          ]}
          height={250}
          width={280}
          margin={{
            top: 10,
            bottom: 10,
            left: 10,
            right: 10,
          }}
          slotProps={{
            legend: { hidden: true },
          }}
          onItemClick={(event, elementIndex) => {
            if (
              elementIndex !== undefined &&
              processedData[elementIndex.dataIndex]
            ) {
              handleClick(
                processedData[elementIndex.dataIndex],
                elementIndex.dataIndex
              );
            }
          }}
          currentSeriesId={hoveredIndex !== null ? 0 : null}
          currentItemId={hoveredIndex !== null ? hoveredIndex : null}
        />
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 2,
            width: "100%",
            maxWidth: "280px",
          }}
        >
          {processedData.map((item, index) => (
            <Tooltip
              key={`legend-${index}`}
              title={item.label.length > 20 ? item.label : ""}
              placement="right"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: "0.75rem",
                  color: "#4B5563",
                  cursor: "pointer",
                  backgroundColor:
                    selectedSliceIndex === index
                      ? "rgba(0, 0, 0, 0.05)"
                      : hoveredIndex === index
                      ? "rgba(0, 0, 0, 0.02)"
                      : "transparent",
                  padding: "4px 8px",
                  borderRadius: "4px",
                  transition: "all 0.2s ease-in-out",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.08)",
                  },
                }}
                onClick={() => handleClick(item, index)}
                onMouseEnter={() => handleLegendHover(index)}
                onMouseLeave={handleLegendLeave}
              >
                <Box
                  sx={{
                    width: 10,
                    height: 10,
                    borderRadius: "100%",
                    backgroundColor: pieColors[index % pieColors.length],
                    border:
                      selectedSliceIndex === index || hoveredIndex === index
                        ? "2px solid #000"
                        : "none",
                    transition: "all 0.2s ease-in-out",
                    flexShrink: 0,
                  }}
                />
                <Typography
                  variant="caption"
                  noWrap
                  sx={{
                    fontWeight:
                      selectedSliceIndex === index || hoveredIndex === index
                        ? 600
                        : 400,
                    maxWidth: "220px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  {item.label.length > 20
                    ? `${item.label.substring(0, 20)}...`
                    : item.label}{" "}
                  ({((item.value / total) * 100).toFixed(1)}%)
                </Typography>
              </Box>
            </Tooltip>
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default CustomPieChart;
